import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import API_BASE_URL from "../config";
import "./ResetPassword.css";
import "./Auth.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const API_BASE = `${API_BASE_URL}/auth`;

  // States for password reset request mode (without token)
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);

  // States for password reset mode (with token)
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    general: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const verifyToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/verify-reset-token?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        setErrors((prev) => ({ ...prev, general: data.detail || "Invalid token" }));
      }
    } catch (error) {
      console.error("Error verifying token:", error);
      setTokenValid(false);
      setErrors((prev) => ({ ...prev, general: "Error verifying token" }));
    }
  }, [token, API_BASE]);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token, verifyToken]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({ email: "", general: "" });

    if (!email) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
      setIsLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setErrors((prev) => ({ ...prev, email: "Invalid email format" }));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/request-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setRequestSent(true);
      } else {
        setErrors((prev) => ({
          ...prev,
          email: data.detail || "Error requesting password reset",
        }));
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setErrors((prev) => ({
        ...prev,
        general: "An error occurred while requesting password reset. Please try again later.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (!password || password.length < 8) {
      return "Password must contain at least 8 characters";
    }
    if (!/[A-Za-z]/.test(password)) {
      return "Password must contain at least one letter";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({ password: "", confirmPassword: "", general: "" });

    const passwordError = validatePassword(password);
    if (passwordError) {
      setErrors((prev) => ({ ...prev, password: passwordError }));
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match" }));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: token,
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Токен теперь в httpOnly cookie, backend автоматически инвалидирует его
        alert(
          "Password successfully updated! All active sessions have been terminated. Please log in with your new password."
        );
        navigate("/auth");
      } else {
        setErrors((prev) => ({ ...prev, general: data.detail || "Error resetting password" }));
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setErrors((prev) => ({
        ...prev,
        general: "An error occurred while resetting password. Please try again later.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Password reset request mode (without token)
  if (!token) {
    return (
      <div className="reset-password-page">
        <header className="auth-header">
          <div className="auth-header-brand">
            <img src="/logo.png" alt="SoliditySet Logo" className="auth-header-icon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
            <span className="auth-header-title">
              SoliditySet
            </span>
          </div>
        </header>
        <div className="reset-password-card-wrapper">
          <div className="reset-password-card">
            <div
              className={
                requestSent
                  ? "reset-password-header reset-password-header-center"
                  : "reset-password-header"
              }
            >
              {requestSent && (
                <div className="reset-password-icon-circle">
                  <Mail className="reset-password-icon-large" />
                </div>
              )}
              <h1 className="reset-password-title">Password Recovery</h1>
              <p className="reset-password-subtitle">
                {requestSent
                  ? "Check your email. We sent a password recovery link."
                  : "Enter your email and we will send a password recovery link"}
              </p>
            </div>

            {requestSent ? (
              <div>
                <p className="reset-password-info-text">
                  If the email didn't arrive, check your Spam folder or try requesting password reset again.
                </p>
                <Button
                  className="reset-password-button"
                  onClick={() => {
                    setRequestSent(false);
                    setEmail("");
                    setErrors({ email: "", general: "" });
                  }}
                >
                  Request Again
                </Button>
                <button
                  className="reset-password-back-button"
                  onClick={() => navigate("/auth")}
                >
                  ← Back to Sign In
                </button>
              </div>
            ) : (
              <form className="reset-password-form" onSubmit={handleRequestReset}>
                <div className="reset-password-field">
                  <Label htmlFor="reset-email" className="reset-password-label">
                    Email Address
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    className="reset-password-input"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                    }}
                    placeholder="you@example.com"
                    required
                  />
                  {errors.email && (
                    <p className="reset-password-error">{errors.email}</p>
                  )}
                </div>

                {errors.general && (
                  <p className="reset-password-error">{errors.general}</p>
                )}

                <Button
                  type="submit"
                  className="reset-password-button"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send Link"}
                </Button>

                <button
                  type="button"
                  className="reset-password-back-button"
                  onClick={() => navigate("/auth")}
                >
                  ← Back to Sign In
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Password reset mode (with token) - Loading
  if (tokenValid === null) {
    return (
      <div className="reset-password-page">
        <header className="auth-header">
          <div className="auth-header-brand">
            <img src="/logo.png" alt="SoliditySet Logo" className="auth-header-icon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
            <span className="auth-header-title">
              SoliditySet
            </span>
          </div>
        </header>
        <div className="reset-password-card-wrapper">
          <div className="reset-password-card">
            <div className="reset-password-loading">Verifying token...</div>
          </div>
        </div>
      </div>
    );
  }

  // Password reset mode (with token) - Invalid token
  if (tokenValid === false) {
    return (
      <div className="reset-password-page">
        <header className="auth-header">
          <div className="auth-header-brand">
            <img src="/logo.png" alt="SoliditySet Logo" className="auth-header-icon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
            <span className="auth-header-title">
              SoliditySet
            </span>
          </div>
        </header>
        <div className="reset-password-card-wrapper">
          <div className="reset-password-card">
            <div className="reset-password-header reset-password-header-center">
              <h1 className="reset-password-title">Invalid or Expired Link</h1>
              <p className="reset-password-subtitle">{errors.general}</p>
            </div>
            <Button
              className="reset-password-button"
              onClick={() => navigate("/auth")}
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Password reset mode (with token) - Valid token
  return (
    <div className="reset-password-page">
      <header className="auth-header">
        <div className="auth-header-brand">
          <img src="/logo.png" alt="SoliditySet Logo" className="auth-header-icon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
          <span className="auth-header-title">
            SoliditySet
          </span>
        </div>
      </header>
      <div className="reset-password-card-wrapper">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <h1 className="reset-password-title">Password Recovery</h1>
            <p className="reset-password-subtitle">
              Enter a new password for your account
            </p>
          </div>

          <form className="reset-password-form" onSubmit={handleSubmit}>
            <div className="reset-password-field">
              <Label htmlFor="reset-password" className="reset-password-label">
                New Password
              </Label>
              <Input
                id="reset-password"
                type="password"
                className="reset-password-input"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: "" }));
                }}
                placeholder="At least 8 characters with letters"
                required
              />
              {errors.password && (
                <p className="reset-password-error">{errors.password}</p>
              )}
            </div>

            <div className="reset-password-field">
              <Label
                htmlFor="reset-confirm-password"
                className="reset-password-label"
              >
                Confirm Password
              </Label>
              <Input
                id="reset-confirm-password"
                type="password"
                className="reset-password-input"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword)
                    setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                }}
                placeholder="Repeat password"
                required
              />
              {errors.confirmPassword && (
                <p className="reset-password-error">{errors.confirmPassword}</p>
              )}
            </div>

            {errors.general && (
              <p className="reset-password-error">{errors.general}</p>
            )}

            <Button
              type="submit"
              className="reset-password-button"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Password"}
            </Button>
          </form>

          <button
            className="reset-password-back-button"
            onClick={() => navigate("/auth")}
          >
            ← Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

